import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialog } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { TranslateService } from '@ngx-translate/core';
import { Auth } from '@angular/fire/auth';
import { take, switchMap, EMPTY, finalize } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { ConfirmationModalComponent } from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { User } from 'src/app/models/user';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { ERRORS, SUCCESSES } from 'src/app/common/utils/notification-constants';
import { SettingsNotificationService } from 'src/app/settings/shared/services/settings-notifications.service';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { IntegrationsFormService } from '../services/integrations-form.service';
import { FormAction } from '../services/integrations-form.service';

enum GenerationStatus {
  NotGenerated = 'Not Generated',
  GeneratedNow = 'Generated Now',
  GenerateBefore = 'Generate Before',
}

@UntilDestroy()
@Component({
  selector: 'app-user-settings-integrations-api',
  templateUrl: './user-settings-integrations-api.component.html',
  styleUrls: ['./user-settings-integrations-api.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsIntegrationsApiComponent implements OnInit {
  @Input() form!: UntypedFormGroup;
  private user!: User;
  readonly GenerationStatus = GenerationStatus;
  statusMessagesMap!: Record<GenerationStatus, string>;

  generationStatus$ = new BehaviorSubject<GenerationStatus>(GenerationStatus.NotGenerated);
  isActionInProgress$ = new BehaviorSubject<boolean>(false);
  generatedApiKey$ = new BehaviorSubject<string | undefined>(undefined);
  isSchedulingEnabled = false;

  constructor(
    private dialog: MatDialog,
    private userService: UserService,
    private authService: AuthService,
    private auth: Auth,
    private clipboard: Clipboard,
    private notificationToasterService: NotificationToasterService,
    private translateService: TranslateService,
    private integrationsFormService: IntegrationsFormService,
    private settingsNotificationService: SettingsNotificationService,
    private featureManagementService: FeatureManagementService,
  ) {
    this.isSchedulingEnabled = this.featureManagementService.isSchedulingEnabled();
  }

  ngOnInit(): void {
    this.userService.user.pipe(take(1), untilDestroyed(this)).subscribe((userData) => {
      if (userData?.user) {
        this.user = userData.user;
        this.initializeForm();
        this.subscribeToFormActions();
        this.statusMessagesMap = {
          [GenerationStatus.NotGenerated]: this.translateService.instant(
            'No API key has been generated as yet. The key generated will be associated with your {{email}} account',
            { email: this.user.email },
          ),
          [GenerationStatus.GeneratedNow]: this.translateService.instant(
            'Key generated. Copy this key and save it securely - you cannot view this key after you refresh this page',
          ),
          [GenerationStatus.GenerateBefore]: this.translateService.instant(
            'An API key already exists for your {{email}} account',
            { email: this.user.email },
          ),
        };

        if (this.user.generatedApiKey) {
          this.generationStatus$.next(GenerationStatus.GenerateBefore);
        }
      }
    });
  }

  private initializeForm(): void {
    const initialFormValue = this.form.value;
    this.integrationsFormService.registerForm(this.form, initialFormValue);
  }

  /**
   * Subscribe to form actions from the parent component
   */
  private subscribeToFormActions(): void {
    this.integrationsFormService
      .getFormAction$()
      .pipe(untilDestroyed(this))
      .subscribe((action) => {
        if (action === FormAction.SUBMIT) {
          this.handleSubmit();
        }
      });
  }

  /**
   * Handle form submission
   */
  private handleSubmit(): void {
    if (!this.user) {
      return;
    }

    const updatedUser = {
      ...this.user,
      institution: {
        ...this.user.institution,
        settings: {
          ...this.user?.institution.settings,
          ...this.form.value.settings,
        },
      },
    };

    this.userService
      .updateInstitution(updatedUser)
      .pipe(
        take(1),
        finalize(() => this.integrationsFormService.setLoading(false)),
      )
      .subscribe({
        next: () => {
          this.updateUserAndNotify(updatedUser);
          this.integrationsFormService.updateInitialValue(this.form.value);
        },
        error: () => {
          this.settingsNotificationService.showFailureSettingsUpdateNotification();
        },
      });
  }

  private updateUserAndNotify(updatedUser: User): void {
    const userFromService = this.userService.user.getValue();
    this.userService.user.next({
      ...userFromService,
      user: updatedUser,
    });

    this.settingsNotificationService.showSuccessSettingsUpdateNotification();
  }

  generateApiKey(): void {
    if (!this.user) {
      return;
    }

    this.dialog
      .open(ConfirmationModalComponent, {
        data: {
          title: this.translateService.instant('Generate API Key'),
          description: this.translateService.instant(
            'All calls to our API made by this key will act on your behalf. For security reasons, we can only show you this key once. Please copy it and save it securely.',
          ),
          confirmButtonConfig: {
            icon: 'vpn_key',
            iconType: 'mat-icon',
            label: this.translateService.instant('Generate'),
          },
          cancelButtonConfig: {
            icon: 'close',
            iconType: 'mat-icon',
            label: this.translateService.instant('Cancel'),
          },
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        switchMap((output: 'confirm' | 'cancel') => {
          if (output === 'confirm') {
            this.isActionInProgress$.next(true);
            return this.userService.updateUser({
              _id: this.user?._id,
              generatedApiKey: true,
            });
          }
          return EMPTY;
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.generationStatus$.next(GenerationStatus.GeneratedNow);
          this.generatedApiKey$.next(this.retrieveApiKey());
          this.isActionInProgress$.next(false);
        },
        error: () => {
          this.notificationToasterService.showDefaultErrorNotificationV2(
            this.translateService.instant('API key generation failed'),
            '',
            ERRORS.API_KEY_GENERATION_FAILED,
            undefined,
            undefined,
            undefined,
            true,
          );
          this.isActionInProgress$.next(false);
        },
      });
  }

  retrieveApiKey(): string {
    return this.authService.getRefreshToken();
  }

  copyApiKey(): void {
    this.clipboard.copy(this.generatedApiKey$.getValue() ?? '');
    this.notificationToasterService.showDefaultSuccessNotification(
      this.translateService.instant('API key copied'),
      SUCCESSES.API_KEY_COPIED,
    );
  }

  revokeApiKey(): void {
    if (!this.user) {
      return;
    }

    this.dialog
      .open(ConfirmationModalComponent, {
        data: {
          title: this.translateService.instant('Revoke API Key'),
          description: this.translateService.instant(
            'Are you sure you want to revoke this API key? You will be logged out and need to sign in to Pencil Spaces again.',
          ),
          confirmButtonConfig: {
            icon: 'delete_outline',
            iconType: 'mat-icon',
            label: this.translateService.instant('Revoke'),
            danger: true,
          },
          cancelButtonConfig: {
            icon: 'close',
            iconType: 'mat-icon',
            label: this.translateService.instant('Cancel'),
          },
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        switchMap((output: 'confirm' | 'cancel') => {
          if (output === 'confirm' && this.auth.currentUser?.uid) {
            this.isActionInProgress$.next(true);
            return this.userService.revokeRefreshTokens(this.user._id);
          }
          return EMPTY;
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: () => {
          this.authService.signoutAndRedirect();
        },
        error: () => {
          this.notificationToasterService.showDefaultErrorNotificationV2(
            this.translateService.instant('API key revocation failed'),
            '',
            ERRORS.API_KEY_REVOCATION_FAILED,
            undefined,
            undefined,
            undefined,
            true,
          );
          this.isActionInProgress$.next(false);
        },
      });
  }
}
